import React, { useState } from 'react';
import {
  Camera, Upload, Save, Share2, Phone, Mail, Globe,
  Linkedin, Twitter, Instagram, Github, Edit3, Eye,
  X, Check, User, MapPin, Briefcase, Lock, LogIn, AlertCircle,
  Plus, Trash2, ArrowLeft, Users, ExternalLink, RefreshCw,
  Download, FileText, Calendar, Video, Music, ShoppingCart,
  Link as LinkIcon, Youtube, Facebook, MessageCircle, Sun, Moon,
  ChevronUp, ChevronDown, GripVertical, Settings
} from 'lucide-react';

// Maps icon name strings to Lucide React icon components
export const ICON_MAP: Record<string, import('lucide-react').LucideIcon> = {
  camera: Camera,
  upload: Upload,
  save: Save,
  share: Share2,
  phone: Phone,
  mail: Mail,
  globe: Globe,
  linkedin: Linkedin,
  twitter: Twitter,
  instagram: Instagram,
  github: Github,
  edit: Edit3,
  eye: Eye,
  x: X,
  check: Check,
  user: User,
  mapPin: MapPin,
  briefcase: Briefcase,
  lock: Lock,
  login: LogIn,
  alert: AlertCircle,
  plus: Plus,
  trash: Trash2,
  arrowLeft: ArrowLeft,
  users: Users,
  externalLink: ExternalLink,
  refresh: RefreshCw,
  download: Download,
  file: FileText,
  calendar: Calendar,
  video: Video,
  music: Music,
  shoppingCart: ShoppingCart,
  link: LinkIcon,
  youtube: Youtube,
  facebook: Facebook,
  message: MessageCircle,
  sun: Sun,
  moon: Moon,
  chevronUp: ChevronUp,
  chevronDown: ChevronDown,
  grip: GripVertical,
  settings: Settings,
};

export type IconName = keyof typeof ICON_MAP;

interface SocialIconProps {
  url?: string;
  icon?: import('lucide-react').LucideIcon;
  label?: string;
  themeColor?: string;
}

export const SocialIconDisplay = ({ url, icon: Icon, label, themeColor }: SocialIconProps) => {
  if (!url || !Icon) return null;
  return (
    <span
      className={`inline-flex items-center justify-center w-10 h-10 rounded-full bg-opacity-10 ${themeColor || 'bg-indigo-500'}`}
      title={label}
    >
      <Icon className="w-5 h-5" />
    </span>
  );
};

interface ColorSelectorProps {
  selectedColor: string;
  onSelect: (color: string) => void;
  label?: string;
  showAuto?: boolean;
  autoLabel?: string;
}

const COLORS = [
  'indigo', 'blue', 'rose', 'emerald', 'slate', 'purple', 'cyan', 'teal',
  'orange', 'pink', 'violet', 'fuchsia', 'amber', 'lime', 'green', 'yellow', 'red',
];

const COLOR_MAP: Record<string, { bg: string; ring: string; label: string }> = {
  indigo: { bg: 'bg-accent-600', ring: 'ring-accent-600', label: 'Indigo' },
  blue: { bg: 'bg-blue-600', ring: 'ring-blue-600', label: 'Blue' },
  rose: { bg: 'bg-rose-500', ring: 'ring-rose-500', label: 'Rose' },
  emerald: { bg: 'bg-emerald-500', ring: 'ring-emerald-500', label: 'Emerald' },
  slate: { bg: 'bg-slate-700', ring: 'ring-slate-700', label: 'Slate' },
  purple: { bg: 'bg-purple-600', ring: 'ring-purple-600', label: 'Purple' },
  cyan: { bg: 'bg-cyan-600', ring: 'ring-cyan-600', label: 'Cyan' },
  teal: { bg: 'bg-teal-600', ring: 'ring-teal-600', label: 'Teal' },
  orange: { bg: 'bg-orange-500', ring: 'ring-orange-500', label: 'Orange' },
  pink: { bg: 'bg-pink-500', ring: 'ring-pink-500', label: 'Pink' },
  violet: { bg: 'bg-accent-600', ring: 'ring-accent-600', label: 'Violet' },
  fuchsia: { bg: 'bg-fuchsia-600', ring: 'ring-fuchsia-600', label: 'Fuchsia' },
  amber: { bg: 'bg-amber-500', ring: 'ring-amber-500', label: 'Amber' },
  lime: { bg: 'bg-lime-500', ring: 'ring-lime-500', label: 'Lime' },
  green: { bg: 'bg-green-600', ring: 'ring-green-600', label: 'Green' },
  yellow: { bg: 'bg-yellow-500', ring: 'ring-yellow-500', label: 'Yellow' },
  red: { bg: 'bg-red-600', ring: 'ring-red-600', label: 'Red' },
};

export default function ColorSelector({ selectedColor, onSelect, label, showAuto = false, autoLabel = 'Auto (complementary)' }: ColorSelectorProps) {
  const isHex = selectedColor?.startsWith('#');
  const [hexInput, setHexInput] = useState(isHex ? selectedColor : '');
  const [hexError, setHexError] = useState(false);

  function handleHexChange(val: string) {
    setHexInput(val);
    const normalized = val.trim();
    if (/^#[0-9A-Fa-f]{6}$/.test(normalized)) {
      setHexError(false);
      onSelect(normalized);
    } else if (normalized === '' || normalized === '#') {
      setHexError(false);
    } else {
      setHexError(true);
    }
  }

  function handleHexBlur() {
    const normalized = hexInput.trim();
    if (normalized && !/^#[0-9A-Fa-f]{6}$/.test(normalized)) {
      // Try auto-prefix
      const withHash = normalized.startsWith('#') ? normalized : `#${normalized}`;
      if (/^#[0-9A-Fa-f]{6}$/.test(withHash)) {
        setHexInput(withHash);
        setHexError(false);
        onSelect(withHash);
      }
    }
  }

  return (
    <div className="space-y-3">
      {label && <label className="block text-sm font-medium text-zinc-800 dark:text-stone-100">{label}</label>}
      {showAuto && (
        <button
          type="button"
          onClick={() => onSelect('auto')}
          className={`w-full rounded-lg border-2 px-3 py-2 text-sm font-medium transition-colors ${
            selectedColor === 'auto'
              ? 'border-zinc-800 dark:border-stone-100 bg-zinc-100 dark:bg-zinc-700 text-zinc-800 dark:text-stone-100'
              : 'border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-zinc-600 dark:text-stone-400 hover:border-zinc-400'
          }`}
        >
          {autoLabel}
        </button>
      )}
      <div className="flex flex-wrap gap-2">
        {COLORS.map((color) => {
          const { bg, ring, label: colorLabel } = COLOR_MAP[color] ?? COLOR_MAP['indigo']!;
          const isSelected = selectedColor === color;
          return (
            <button
              key={color}
              type="button"
              onClick={() => { onSelect(color); setHexInput(''); setHexError(false); }}
              title={colorLabel}
              className={`w-8 h-8 rounded-full ${bg} ${
                isSelected ? `ring-2 ring-offset-2 ring-offset-white dark:ring-offset-zinc-900 ${ring}` : 'hover:scale-110 transition-transform'
              }`}
            />
          );
        })}
      </div>

      {/* Hex color input */}
      <div className="flex items-center gap-2 pt-1">
        {/* Native color picker */}
        <label className="relative flex-shrink-0 cursor-pointer" title="Farbe wählen">
          <div
            className="w-8 h-8 rounded-full border-2 border-line-strong cursor-pointer overflow-hidden shadow-sm"
            style={{ backgroundColor: isHex ? selectedColor : (hexInput.startsWith('#') && hexInput.length === 7 ? hexInput : '#4f46e5') }}
          />
          <input
            type="color"
            className="absolute inset-0 opacity-0 w-full h-full cursor-pointer"
            value={isHex ? selectedColor : (hexInput.startsWith('#') && hexInput.length === 7 ? hexInput : '#4f46e5')}
            onChange={(e) => { setHexInput(e.target.value); setHexError(false); onSelect(e.target.value); }}
          />
        </label>
        <div className="flex-1 relative">
          <input
            type="text"
            value={hexInput}
            onChange={(e) => handleHexChange(e.target.value)}
            onBlur={handleHexBlur}
            placeholder="#4f46e5"
            maxLength={7}
            className={`w-full rounded-lg bg-slate-100 dark:bg-white/5 border text-sm px-3 py-1.5 text-slate-800 dark:text-white/80 placeholder:text-slate-400 dark:placeholder:text-white/20
                        focus:outline-none focus:ring-1 transition-all font-mono
                        ${hexError
                          ? 'border-rose-500/50 focus:border-rose-500/70 focus:ring-rose-500/20'
                          : isHex
                            ? 'border-accent-500/50 focus:border-accent-500/70 focus:ring-accent-500/20'
                            : 'border-line focus:border-accent-500/50 focus:ring-accent-500/20'
                        }`}
          />
          {hexError && (
            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-rose-400">!</span>
          )}
        </div>
        <span className="text-xs text-ink-faint flex-shrink-0">Hex</span>
      </div>
    </div>
  );
}
